import { Skeleton,Stack,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiError } from "react-icons/bi";
import AttToken from "../helpers/attToken";
import Files from "../service/Files";
import Reports from "../service/Reports";
import type { ChecklistReportType } from "../types/reports/types";

const reportsService = new Reports();
const filesService = new Files();

const ImagesCmpAssinatura = ({ images, selected }: { images: string[], selected: ChecklistReportType }) => {
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const { t } = useTranslation('translation');

  const compressImage = async (blob: Blob, quality = 0.6): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;
        const maxWidth = 800;

        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = height * scale;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (compressedBlob) => {
            if (compressedBlob) {
              const compressedUrl = URL.createObjectURL(compressedBlob);
              resolve(compressedUrl);
            }
          },
          "image/jpeg",
          quality
        );
      };
      img.src = url;
    });
  };

  const fetchImagesInBatches = async (urls: string[], batchSize = 4) => {
    const results: string[] = [];

    for (let i = 0; i < urls.length; i += batchSize) {
      const batch = urls.slice(i, i + batchSize);

      const batchResults = await Promise.all(
        batch.map(async (image) => {
          const response = await fetch(image);

          if (!response.ok) {
            throw new Error(`Erro ao baixar imagem: ${image}`);
          }

          const blob = await response.blob();

          const shouldCompress = blob.size > 500_000;

          if (shouldCompress) {
            return await compressImage(blob);
          }

          return URL.createObjectURL(blob);
        })
      );

      results.push(...batchResults);
    }

    return results;
  };

  const useImages = (images: string[]) => {
    return useQuery({
      queryKey: ['images', images],

      queryFn: async () => {
        const token = await AttToken();

        let urls: string[] = [];
        if (token) {
          const response = await filesService.downloadFiles(token, 'web', '-', images);
          urls = response?.urls || [];
        } else {
          urls = await reportsService.getImages(images || []);
        }

        if (!urls.length) return [];

        return await fetchImagesInBatches(urls, 4);
      },

      enabled: images?.length > 0,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    });
  };

  const { data: returnedImg = [], isLoading } = useImages(images);

  if (imageErrors[selected?.id as string]) {
    return (
      <Stack sx={{ border: '1px gray solid', borderRadius: 2 }} direction='column' justifyContent='center' alignItems='center' width={230} height={120} color='gray'>
        <BiError size={25} />
        <Typography color='textDisabled' fontWeight={700} component={'span'} textAlign='center'>
          {t('users.imageNotFound')}
        </Typography>
      </Stack>);
  }

  return (isLoading)
    ? (<Skeleton variant="rounded" width={230} height={130} />)
    : (<img
      onError={() => setImageErrors(prev => ({ ...prev, [selected?.id as string]: true }))}
      src={returnedImg?.[0]} style={{
        width: `${!selected?.preenchimentoWeb ? '120px' : '230px'}`,
        height: `${!selected?.preenchimentoWeb ? '230px' : '120px'}`,
        transform: `${!selected.preenchimentoWeb && 'rotate(-90deg) translate(50px, 50px)'}`,
        marginBottom: `${!selected.preenchimentoWeb && '-100px'}`
      }} alt="assinatura" />
    );
};

export default ImagesCmpAssinatura;